import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import { setAuthToken } from '../auth/authUtils';
import axiosInstance from '../axiosInstance';

const useRegisterForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [socialProviders, setSocialProviders] = useState([]);

  useEffect(() => {
    const accepted = localStorage.getItem('cookie_consent');
    if (!accepted) setShowCookieNotice(true);
  }, []);

  // Handle social auth callback (token in URL params after OAuth redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const socialAuth = params.get('social_auth');
    const token = params.get('token');
    const socialError = params.get('error');

    if (socialAuth === 'success' && token) {
      setAuthToken(token, true);
      const expiresAt = params.get('expires_at');
      if (expiresAt) localStorage.setItem('token_expires_at', expiresAt);

      axiosInstance.get('/user', {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        dispatch(login({ user: res.data, token }));
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) {
          localStorage.removeItem('promo_returnTo');
          navigate(promoReturn, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      }).catch(() => {
        dispatch(login({ user: {}, token }));
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) {
          localStorage.removeItem('promo_returnTo');
          navigate(promoReturn, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      });
    } else if (socialAuth === 'error' && socialError) {
      setError(socialError);
    }
  }, []);

  // Fetch available social providers
  useEffect(() => {
    axiosInstance.get('/auth/social/providers').then(res => {
      if (res.data?.success) setSocialProviders(res.data.data || []);
    }).catch(() => {});
  }, []);

  const handleSocialLogin = (provider) => {
    // Save returnTo before OAuth redirect (query params are lost during redirect)
    const urlParams = new URLSearchParams(location.search);
    const returnTo = urlParams.get('returnTo');
    if (returnTo) {
      localStorage.setItem('promo_returnTo', returnTo);
    }
    const apiBase = axiosInstance.defaults.baseURL || '';
    window.location.href = `${apiBase}/auth/social/${provider}/redirect`;
  };

  const clearFieldError = (field) => {
    if (error) setError('');
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleUsernameChange = (value) => {
    setUsername(value);
    clearFieldError('username');
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    clearFieldError('email');
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    clearFieldError('password');
  };

  const handlePasswordConfirmationChange = (value) => {
    setPasswordConfirmation(value);
    clearFieldError('password_confirmation');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setValidationErrors({});

    try {
      const response = await axiosInstance.post('/register', {
        username,
        email,
        password,
        password_confirmation: password,
      });

      if (response.status === 201 && response.data.success) {
        const { user, token } = response.data.data;

        setAuthToken(token, true);
        dispatch(login({ user, token }));

        // Return-destination priority:
        //   1. ?returnTo= URL param (from promo / share / deep link)
        //   2. localStorage.promo_returnTo (survives OAuth redirect)
        //   3. location.state.from.pathname (from AuthWrapper's redirect
        //      after a session-expired auth wall)
        //   4. /home (default landing)
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');
        const promoReturn = localStorage.getItem('promo_returnTo');
        if (promoReturn) localStorage.removeItem('promo_returnTo');
        const fromState = location.state?.from?.pathname
          ? location.state.from.pathname + (location.state.from.search || '')
          : null;
        const destination = returnTo || promoReturn || fromState || '/home';

        // Redirect to email verification if required, but PRESERVE the
        // final destination so the verify page can hand it back.
        if (response.data.data.email_verification_required) {
          const verifyUrl = destination && destination !== '/home'
            ? `/verify-email?returnTo=${encodeURIComponent(destination)}`
            : '/verify-email';
          navigate(verifyUrl, { replace: true });
        } else {
          navigate(destination, { replace: true });
        }
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        if (errors) {
          setValidationErrors(errors);
          errorMessage = 'Please correct the errors below.';
        } else {
          errorMessage = err.response.data.message || 'Validation failed.';
        }
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data.message || 'Invalid request. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAll = () => {
    localStorage.setItem('cookie_consent', 'all');
    setShowCookieNotice(false);
  };

  const handleOnlyNecessary = () => {
    localStorage.setItem('cookie_consent', 'necessary');
    setShowCookieNotice(false);
  };

  return {
    username, email, password, passwordConfirmation,
    showPassword, showConfirmPassword, loading, error, validationErrors,
    showCookieNotice,
    setShowPassword, setShowConfirmPassword,
    handleUsernameChange,
    handleEmailChange, handlePasswordChange, handlePasswordConfirmationChange,
    handleSubmit,
    handleAcceptAll, handleOnlyNecessary,
    socialProviders, handleSocialLogin,
  };
};

export default useRegisterForm;
